import type { Locale } from '@/lib/i18n/config'
import { dateTimeFormat } from '@/lib/i18n/format'
import type { SkyPresetName } from '../prompt-showcase/golden-hour-sky'
import type { AppVisualChrome } from './app-visual-stage'
import type { VisualsTranslator } from './visual-copy'

/**
 * The marketing pages' app visuals, one per section that shows the product:
 * the home page's five, then the audience pages' (`/marketers`, `/startups`,
 * `/agencies`, `/ecommerce`).
 */
export type AppVisualKind =
  | 'brandGuide'
  | 'canvas'
  | 'send'
  | 'automation'
  | 'mcp'
  | 'metrics'
  | 'liveMetrics'
  | 'contacts'
  | 'collaboration'
  | 'growthTools'
  | 'developer'
  | 'brands'
  | 'remix'
  | 'figma'
  | 'shopify'

export type AppVisualLayout = {
  /** The stage's true-scale size; the page scales it to the column. */
  width: number
  height: number
  chrome: AppVisualChrome
  innerClassName?: string
  /** The sky behind the scene; neighbouring visuals never share one. */
  sky: SkyPresetName
  /**
   * A working copy of the product the visitor can click and edit, or (when
   * false) a picture of it, `inert` and labelled as one image
   * (`appVisualLabel`).
   */
  isInteractive: boolean
}

/** Every contact in the contacts visual (`audience/contacts-scene.tsx`). */
export const CONTACTS_VISUAL_TOTAL = 1_284_902

/** The hour the send visual schedules its launch for, tomorrow (`send/send-modal.tsx`). */
export const SEND_VISUAL_HOUR = 9

/** That hour on any day: the description shows only its time. */
const SEND_VISUAL_TIME = Date.UTC(2026, 0, 1, SEND_VISUAL_HOUR)

/**
 * What a visual shows, in the reader's language: its frame's accessible
 * name (`marketingVisuals.labels`). The send time and the contacts total
 * format in `locale`.
 */
export function appVisualLabel(
  t: VisualsTranslator,
  kind: AppVisualKind,
  locale: Locale
): string {
  return t(`labels.${kind}`, {
    time: dateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
    }).format(SEND_VISUAL_TIME),
    count: CONTACTS_VISUAL_TOTAL,
  })
}

/**
 * Stage size, chrome and sky per visual. Kept apart from the scenes so the
 * page and the lab can lay a stage out without loading any scene's code.
 */
export const APP_VISUAL_LAYOUT: Record<AppVisualKind, AppVisualLayout> = {
  brandGuide: {
    width: 1024,
    height: 680,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: true,
  },
  canvas: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: true,
  },
  send: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigoldLate',
    isInteractive: true,
  },
  automation: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: true,
  },
  mcp: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: true,
  },
  metrics: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: false,
  },
  liveMetrics: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigoldLate',
    isInteractive: false,
  },
  contacts: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: false,
  },
  collaboration: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'goldenHour',
    isInteractive: false,
  },
  growthTools: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: false,
  },
  developer: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: false,
  },
  brands: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigoldLate',
    isInteractive: false,
  },
  remix: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'apricot',
    isInteractive: false,
  },
  figma: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'goldenHour',
    isInteractive: false,
  },
  shopify: {
    width: 1024,
    height: 560,
    chrome: 'bare',
    sky: 'marigold',
    isInteractive: false,
  },
}
